import type {
  CardDisplayType,
  CardId,
  CardType,
  DashboardId,
  DatasetQuery,
  DraftTransform,
  PaginationResponse,
  RowValue,
  SuggestedTransform,
  Transform,
  UnsavedCard,
} from ".";

export type MetabotFeedbackType =
  | "great"
  | "wrong_data"
  | "incorrect_result"
  | "invalid_sql";

/* Metabot v3 - Base Types */

export type MetabotCodeEditorBufferContext = {
  id: string;
  source: Record<string, unknown> & {
    language: "sql";
    database_id: number | null;
  };
  cursor: { line: number; column: number };
  selection?: {
    text: string;
    start: { line: number; column: number };
    end: { line: number; column: number };
  };
};

export type MetabotCodeEditorContext = {
  type: "code_editor";
  buffers: MetabotCodeEditorBufferContext[];
};

export type MetabotUserIsViewingContext = Array<
  MetabotEntityInfo | MetabotCodeEditorContext
>;

export type MetabotChatContext = {
  user_is_viewing: MetabotUserIsViewingContext;
  current_time_with_timezone: string;
  default_database_id?: number;
  capabilities: string[];
  code_editor?: MetabotCodeEditorContext;
};

export type MetabotTool = {
  name: string; // TODO: make strictly typed - currently there's no tools
  parameters: Record<string, any>;
};

export type MetabotHistoryUserMessageEntry = {
  role: "user";
  message: string;
  context: MetabotChatContext;
};

export type MetabotHistoryToolEntry = {
  role: "assistant";
  assistant_response_type: "tools";
  tools: MetabotTool[];
};

export type MetabotHistoryMessageEntry = {
  role: "assistant";
  assistant_response_type: "message";
  message: string;
};

export type MetabotHistoryEntry =
  | MetabotHistoryUserMessageEntry
  | MetabotHistoryToolEntry
  | MetabotHistoryMessageEntry;

export type MetabotHistory = any[];

export type MetabotStateContext = Record<string, any>;

export type MetabotColumnType =
  | "number"
  | "string"
  | "date"
  | "datetime"
  | "time"
  | "boolean"
  | "null";
export type MetabotColumnInfo = {
  name: string;
  type?: MetabotColumnType;
};

export type MetabotSeriesConfig = {
  x: MetabotColumnInfo;
  y?: MetabotColumnInfo;
  x_values?: RowValue[];
  y_values?: RowValue[];
  display_name: string;
  chart_type: CardDisplayType;
  stacked?: boolean;
};

export type MetabotChartConfig = {
  image_base_64?: string;
  title?: string | null;
  description?: string | null;
  data?: Array<{
    columns: Array<MetabotColumnInfo>;
    rows: Array<Array<string | number>>;
  }>;
  series?: Record<string, MetabotSeriesConfig>;
  timeline_events?: Array<{
    name: string;
    description?: string;
    timestamp: string;
  }>;
  query?: DatasetQuery;
  display_type?: CardDisplayType;
};

export type MetabotCardInfo = {
  type: CardType;
  id: CardId;
  chart_configs?: Array<MetabotChartConfig>;
  query?: DatasetQuery;
  error?: any;
};

export type MetabotDashboardInfo = {
  type: "dashboard";
  id: DashboardId;
};

export type MetabotAdhocQueryInfo = {
  type: "adhoc";
  chart_configs?: Array<MetabotChartConfig>;
  query?: DatasetQuery;
  error?: any;
};

export type MetabotDocumentInfo = {
  type: "document";
  id: number;
};

export type MetabotTransformInfo =
  | ({ type: "transform"; error?: string } & Transform) // edit
  | ({ type: "transform"; error?: string } & SuggestedTransform) // edit saved suggested
  | ({ type: "transform"; error?: string } & DraftTransform); // edit unsaved suggested

export type MetabotEntityInfo =
  | MetabotCardInfo
  | MetabotDashboardInfo
  | MetabotAdhocQueryInfo
  | MetabotDocumentInfo
  | MetabotTransformInfo;

export type MetabotCodeEdit = {
  buffer_id: string;
  mode: "rewrite";
  value: string;
  active?: boolean;
};

/* Metabot v3 - API Request Types */

export type MetabotAgentRequest = {
  message: string;
  context: MetabotChatContext;
  history: MetabotHistory;
  state: MetabotStateContext;
  conversation_id: string; // uuid
  metabot_id?: string;
  profile_id?: string;
};

export type MetabotAgentResponse = {
  history: MetabotHistory[];
  conversation_id: string;
  state: any;
};

export type MetabotProvider =
  | "metabase"
  | "anthropic"
  | "azure"
  | "bedrock"
  | "openai"
  | "openrouter";

export interface BedrockCredentials {
  "access-key-id"?: string | null;
  "secret-access-key"?: string | null;
  region?: string | null;
  "session-token"?: string | null;
}

export interface AzureCredentials {
  "api-key"?: string | null;
  "base-url"?: string | null;
}

/** One permissive map mirroring the backend's request schema: Bedrock sends AWS key
 * material, Azure sends an API key and base URL. */
export interface MetabotCredentials
  extends BedrockCredentials, AzureCredentials {}

export interface MetabotSettingsResponse {
  value: string | null;
  "credentials-error"?: string | null;
  models: {
    id: string;
    display_name: string;
    group?: string | null;
  }[];
}

export interface UpdateMetabotSettingsRequest {
  provider: MetabotProvider;
  model?: string;
  "api-key"?: string | null;
  credentials?: MetabotCredentials | null;
}

/* Metabot - Suggested Prompts */

export type SuggestedMetabotPrompt = {
  id: number;
  metabot_id: MetabotId;
  prompt: string;
  model: "metric" | "model";
  model_id: CardId;
  model_name: string;
  created_at: string;
  updated_at: string;
};

type BaseSuggestedMetabotPromptsRequest = {
  metabot_id: MetabotId;
  model?: string;
  model_id?: number;
};

export type SuggestedMetabotPromptsRequest =
  BaseSuggestedMetabotPromptsRequest & {
    metabot_id: MetabotId;
    model?: string;
    model_id?: number;
  } & (
      | { sample?: void; limit?: number | null; offset?: number | null }
      | { sample: true; limit?: number | null; offset?: void }
    );

export type SuggestedMetabotPromptsResponse = {
  prompts: SuggestedMetabotPrompt[];
} & PaginationResponse;

export type DeleteSuggestedMetabotPromptRequest = {
  metabot_id: MetabotId;
  prompt_id: SuggestedMetabotPrompt["id"];
};

export type RegenerateSuggestedMetabotPromptsResponse =
  | { status: "generated"; prompt_count: number }
  | { status: "no-library-content" }
  | { status: "ai-produced-no-prompts" };

export const METABOT_ISSUE_TYPE_VALUES = [
  "ui-bug",
  "took-incorrect-actions",
  "overall-refusal",
  "did-not-follow-request",
  "not-factual",
  "incomplete-response",
  "other",
] as const;

export type MetabotIssueType = (typeof METABOT_ISSUE_TYPE_VALUES)[number];

export type MetabotFeedback = {
  metabot_id: MetabotId;
  message_id: string;
  freeform_feedback?: string;
} & ({ positive: true } | { positive: false; issue_type?: MetabotIssueType });

export type MetabotSourceType = "table" | "card" | "model";

export type MetabotSourceFeedback = {
  metabot_id: MetabotId;
  message_id: string;
  source_id: number;
  source_type: MetabotSourceType;
  positive: boolean;
};

/**
 * Feedback payload for MCP Apps visualization results.
 *
 * Sends the prompt and query context needed for Harbormaster
 * to understand the generated visualization.
 */
export type McpAppsFeedback = {
  /** User's rating and optional comments about the generated visualization. */
  feedback: {
    positive: boolean;

    /** Client-generated id for feedback submission. */
    message_id: string;

    /** Optional category for negative feedback. */
    issue_type?: string;

    /** Optional free-form user feedback text. */
    freeform_feedback?: string;
  };

  /** MCP-specific context that Harbormaster needs to evaluate the result. */
  conversation_data: {
    /** Identifies this submission as coming from the MCP Apps flow. */
    source: "mcp";

    /** User prompt that produced the visualization, when available. */
    prompt: string | null;

    /** Query text or structured query snapshot for the result, when available. */
    query: string | null;
  };
};

export type SubmitMcpAppsFeedbackRequest = {
  mcpSessionId: string;
  payload: McpAppsFeedback;
};

/* Metabot v3 - Entity Types */

export type MetabotId = number;
export type MetabotName = string;

export type MetabotInfo = {
  id: MetabotId;
  entity_id: string;
  name: MetabotName;
  description: string;
  use_verified_content: boolean;
  collection_id: number | null;
  created_at: string;
  updated_at: string;
};

/* Metabot v3 - Document Types */

export interface MetabotGenerateContentRequest {
  instructions: string;
  references?: Record<string, string>;
}

export interface MetabotGenerateContentResponse {
  draft_card: (UnsavedCard & { name?: string }) | null;
  description: string;
  error: string | null;
}

/* Metabot v3 - Data Part Types */

export type MetabotTodoItem = {
  id: string;
  content: string;
  status: "pending" | "in_progress" | "completed" | "cancelled";
  priority: "high" | "medium" | "low";
};

/* Metabot v3 - Slack Settings */

export type MetabotSlackSettings =
  | {
      "slack-connect-client-id": string;
      "slack-connect-client-secret": string;
      "metabot-slack-signing-secret": string;
    }
  | {
      "slack-connect-client-id": null;
      "slack-connect-client-secret": null;
      "metabot-slack-signing-secret": null;
    };

/* Metabot v3 - Group Permissions */

export enum AIToolKey {
  Metabot = "permission/metabot",
  ChatAndNLQ = "permission/metabot-nlq",
  SQLGeneration = "permission/metabot-sql-generation",
  OtherTools = "permission/metabot-other-tools",
}

export type MetabotGroupPermission = {
  group_id: number;
  perm_type: AIToolKey;
  perm_value: "yes" | "no";
};

export type MetabotPermissionsResponse = {
  permissions: MetabotGroupPermission[];
  advanced: boolean;
};

export type UpdateMetabotPermissionsRequest = {
  permissions: MetabotGroupPermission[];
};

export type UserMetabotPermissions = {
  metabot: "yes" | "no";
  "metabot-sql-generation": "yes" | "no";
  "metabot-nlq": "yes" | "no";
  "metabot-other-tools": "yes" | "no";
};

export type UserMetabotPermissionsResponse = {
  permissions: UserMetabotPermissions;
};

export type MetabotLimitPeriod = "daily" | "weekly" | "monthly";
export type MetabotLimitType = "tokens" | "messages";

/* Metabot v3 - Usage Limits */

export type MetabotInstanceLimit = { max_usage: number | null };
export type MetabotGroupLimit = { group_id: number; max_usage: number };
export type MetabotTenantLimit = {
  tenant_id: number;
  max_usage: number | null;
};
